import { useQuery, useQueryClient, useMutation } from "@tanstack/react-query";
import axios from "axios";
import { Apartment } from "./types/apartment";
import toast from "react-hot-toast";
import { useCallback } from "react";

// Define the interface for expense summary data
export interface ExpenseSummaryData {
  perPropertyMonthly: {
    [propertyName: string]: {
      [month: string]: number;
    };
  };
  monthlyTotalAllProperties: {
    [month: string]: number;
  };
  perPropertyYearlyTotal: {
    [propertyName: string]: number;
  };
  grandTotal: number;
}

export const useApartment = () => {
  const queryClient = useQueryClient();
  const toastId = "apartment-toast";


  const Apartment = (params: { 
  active: boolean | null, 
  delete: boolean, 
}) => useQuery({
  queryKey: ["apartments", params],
  queryFn: async ({ queryKey }) => {
    try {
      const { active, delete: deleteParam } = queryKey[1] as any;
      
      // Prepare request parameters
      const requestParams: any = {};
      
      if (deleteParam) {
        // For deleted items, only send deleted parameter
        requestParams.deleted = 1;
      } else {
        // For active/inactive items, send status parameter
        if (active === true) {
          requestParams.status = 'ACTIVE';
        } else if (active === false) {
          requestParams.status = 'INACTIVE';
        }
        // Don't send status parameter when active is null (shouldn't happen for non-deleted)
      }
      
      const response = await axios.get(`/api/v1/getActiveInActiveDeleted`, {
        params: requestParams,
      });
      
      // Map the API response to match your frontend structure
      const mappedData = {
        ...response.data,
        data: {
          ...response.data.data,
          content: response.data.data.content.map((apartment: any) => ({
            id: apartment.id,
            apartmentName: apartment.appartmentName,
            apartmentAddress: apartment.appratmentAddres,
            appartmentType: apartment.appartmentType,
            isActive: apartment.appartmentStatus === 'ACTIVE',
            apartmentImageUrl: apartment.apartmentImageUrl || '',
        propertyHoldings: apartment.propertyHoldings ? apartment.propertyHoldings.map((prop: any) => ({
              id: prop.id,
              propertyName: prop.name || prop.propertyName || '' // Use appropriate field name from API
            })) : [],
          }))
        }
      };
    //  console.log("map",mappedData)
      return mappedData;
    } catch (error) {
      console.error("Error fetching apartments:", error);
      throw error;
    }
  },
  staleTime: 30000,
  retry: false,
});


  // GET properties in apartment
  const useGetPropertiesInApartment = (apartmentId: number) => useQuery({
    queryKey: ["propertiesInApartment", apartmentId],
    queryFn: async () => {
      try {
        const response = await axios.get(`/api/v1/getPropertyInApartment/${apartmentId}`);
        return response.data;
      } catch (error) {
        console.error("Error fetching properties in apartment:", error);
        throw error;
      }
    },
    enabled: !!apartmentId,
    staleTime: 30000,
    retry: false,
  });

  // GET tenants in property
  const useGetTenantsInProperty = (propertyId: number) => useQuery({
    queryKey: ["tenantsInProperty", propertyId],
    queryFn: async () => {
      try {
        const response = await axios.get(`/api/v1/getAllTenantsInProperty/${propertyId}`);
        return response.data;
      } catch (error) {
        console.error("Error fetching tenants in property:", error);
        throw error;
      }
    },
    enabled: !!propertyId,
    staleTime: 30000,
    retry: false,
  });

  // GET tenants in apartment
  const useGetTenantsInApartment = (apartmentId: number) => useQuery({
    queryKey: ["tenantsInApartment", apartmentId],
    queryFn: async () => {
      try {
        const response = await axios.get(`/api/v1/getAllTenantsInApartment/${apartmentId}`);
        return response.data;
      } catch (error) {
        console.error("Error fetching tenants in apartment:", error);
        throw error;
      }
    },
    enabled: !!apartmentId,
    staleTime: 30000,
    retry: false,
  });

  // POST filter expenses
  const filterExpenses = useMutation({
    mutationFn: async (filterData: {
      appartmentId?: number;
      propertyName?: string;
      tenantName?: string;
      expenseType?: string;
      minAmount?: number;
      maxAmount?: number;
      fromDate?: string;
      toDate?: string;
    }) => {
      const response = await axios.post(`/api/v1/expenses/filter`, filterData);
    //  console.log('data for ex', response.data)
      return response.data;
    },
    onError: (error) => {
      console.error("Error filtering expenses:", error);
      toast.error(
        error.message
          ? `${error.message}`
          : "Sorry, Failed to filter expenses, please try again",
        { id: toastId }
      );
    },
    onSuccess: () => {
      toast.success("Expenses filtered successfully.", { id: toastId });
    },
  });

  // GET properties not in any apartment
  const getPropertiesNotInApartment = useQuery({
    queryKey: ["propertiesNotInApartment"],
    queryFn: async () => {
      try {
        const response = await axios.get(`/api/v1/getPropertyNotInApartment`);
        return response.data;
      } catch (error) {
        console.error("Error fetching properties not in apartment:", error);
        throw error;
      }
    },
    staleTime: 30000,
    retry: false,
  });

  // POST new apartment
  // const addApartment = useMutation({
  //   mutationFn: async (newApartment: Omit<Apartment, 'id'>) => {
  //     // Prepare the JSON payload
  //     const payload = {
  //       appartmentName: newApartment.apartmentName,
  //       appratmentAddres: newApartment.apartmentAddress,
  //       appartmentStatus: newApartment.isActive ? "ACTIVE" : "INACTIVE",
  //       appartmentType: "RESIDENTIAL", // Default type or make it configurable
  //       propertyHoldings: newApartment.propertyHoldings.map(id => id.toString())
  //     };

  //     const response = await axios.post(`/api/v1/addAppartment`, payload, {
  //       headers: {
  //         'Content-Type': 'application/json',
  //       },
  //     });
  //     return response.data;
  //   },
  //   onError: (error) => {
  //     console.error("Error adding apartment:", error);
  //     toast.error(
  //       error.message
  //         ? `${error.message}`
  //         : "Sorry, Failed to add Apartment, please try again",
  //       { id: toastId }
  //     );
  //   },
  //   onSuccess: () => {
  //     queryClient.invalidateQueries({ queryKey: ["apartments"] });
  //     toast.success("Apartment has been successfully added.", { id: toastId });
  //   },
  // });


    // POST new apartment with form-data
  const addApartment = useMutation({
    mutationFn: async (newApartment: Omit<Apartment, 'id'> & { imageFile?: File }) => {
      const formData = new FormData();
      
      // Append text fields
      formData.append('appartmentName', newApartment.apartmentName);
      formData.append('appratmentAddres', newApartment.apartmentAddress);
      formData.append('appartmentStatus', newApartment.isActive ? "ACTIVE" : "INACTIVE");
      formData.append('appartmentType', newApartment.appartmentType);
      
      // Append property holdings as multiple fields
      newApartment.propertyHoldings.forEach((id, index) => {
        formData.append(`propertyHoldings`, id.toString());
      });
      
      // Append image file if provided
      if (newApartment.apartmentImage) {
        formData.append('apartmentImage', newApartment.apartmentImage);
      }
      
      const response = await axios.post(`/api/v1/addAppartment`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      return response.data;
    },
    onError: (error) => {
      console.error("Error adding apartment:", error);
      toast.error(
        error.message
          ? `${error.message}`
          : "Sorry, Failed to add Apartment, please try again",
        { id: toastId }
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["apartments"] });
      toast.success("Apartment has been successfully added.", { id: toastId });
    },
  });

  // UPDATE apartment with form-data
// UPDATE apartment with form-data
const updateApartment = useMutation({
  mutationFn: async ({ 
    apartmentId, 
    updatedApartment 
  }: { 
    apartmentId: string; 
    updatedApartment: Omit<Apartment, 'id'> & { imageFile?: File }
  }) => {
    const formData = new FormData();
    
    // Append text fields
    formData.append('appartmentName', updatedApartment.apartmentName);
    formData.append('appratmentAddres', updatedApartment.apartmentAddress);
    formData.append('appartmentStatus', updatedApartment.isActive ? "ACTIVE" : "INACTIVE");
    formData.append('appartmentType', updatedApartment.appartmentType);
    
    // Append property holdings as multiple fields
    updatedApartment.propertyHoldings.forEach((property, index) => {
      const propertyId = typeof property === 'object' ? property.id : property;
      formData.append(`propertyHoldings`, propertyId.toString());
    });
    
    // Append image file if provided
    if (updatedApartment.apartmentImage) {
      formData.append('apartmentImage', updatedApartment.apartmentImage);
    }
    
    const response = await axios.put(`/api/v1/updateAppartment/${apartmentId}`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },
  onError: (error) => {
    console.error("Error updating apartment:", error);
    toast.error(
      error.message
        ? `${error.message}`
        : "Sorry, Failed to update Apartment, please try again",
      { id: toastId }
    );
  },
  onSuccess: () => {
    queryClient.invalidateQueries({ queryKey: ["apartments"] });
    toast.success("Apartment has been successfully updated.", { id: toastId });
  },
});

  // Function to get apartment image URL
  const getApartmentImageUrl = useCallback(async (apartmentId: string | number) => {
    try {
      const response = await axios.get(`/api/v1/apartment/${apartmentId}/image`, {
        responseType: 'blob' // Important for image responses
      });
      
      // Create a blob URL from the response
      const blob = new Blob([response.data], { type: response.headers['content-type'] });
      return URL.createObjectURL(blob);
    } catch (error) {
      console.error("Error fetching apartment image:", error);
      throw error;
    }
  }, []);

  

  // GET apartment by ID
  const getApartmentById = useMutation({
    mutationFn: async (apartmentId: string) => {
      try {
        const response = await axios.get(`/api/v1/getAppartment/${apartmentId}`);
        return response.data;
      } catch (error) {
        console.error("Error fetching apartment by ID:", error);
        throw error;
      }
    },
  });



  // DELETE apartment
  const deleteApartment = useMutation({
    mutationFn: async (apartmentId: string) => {
      const response = await axios.delete(`/api/v1/deleteAppartment/${apartmentId}`);
      return response.data;
    },
    onError: (error) => {
      console.error("Error deleting apartment:", error);
      toast.error(
        error.message
          ? `${error.message}`
          : "Sorry, Failed to delete Apartment, please try again",
        { id: toastId }
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["apartments"] });
      toast.success("Apartment has been successfully deleted.", { id: toastId });
    },
  });

  // Create a proper custom hook for apartment expense summary
  const useApartmentExpenseSummary = (apartmentId: string | number, year: number) => {
    return useQuery({
      queryKey: ["apartmentExpenseSummary", apartmentId, year],
      queryFn: async () => {
        try {
          const response = await axios.get(`/api/v1/apartment_expense_summary`, {
            params: {
              apartmentId,
              year
            }
          });
          return response.data;
        } catch (error) {
          console.error("Error fetching apartment expense summary:", error);
          throw error;
        }
      },
      enabled: !!apartmentId,
      staleTime: 5 * 60 * 1000,
    });
  };

  return { 
    Apartment,
    useGetPropertiesInApartment,
    useGetTenantsInProperty,
    useGetTenantsInApartment,
    filterExpenses,
    getPropertiesNotInApartment,
    addApartment,
    getApartmentById,
    updateApartment,
    deleteApartment,
    useApartmentExpenseSummary, 
    getApartmentImageUrl,
    queryClient
  };
};